import { Anchor, Footprints, MessageCircle } from 'lucide-react';
import { HOME } from '@/constants/testIds';

// Section formerly "Adaptive Cycling" — repositioned around the strategic
// pivot: away from clinical / expert / adaptive-specialist language, into
// **Practical Terrain & Utility Advice**. Same inclusive spirit, but the
// framing now speaks to everyone who has ever tried to open a gate in the
// rain — not just those with a specific access need.

// Brochure-cover image (Eahora bike parked in front of a Highland-style
// stone church) — replaces the earlier stock "group of on-road cyclists"
// photo, which didn't fit the brand.
const TERRAIN_IMG = '/brand/brochure-cover.png';

export default function Adaptive() {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      // Kept id="adaptive" so existing nav / anchor links don't break.
      id="adaptive"
      data-testid={HOME.adaptiveSection}
      className="section bg-[var(--brand-forest)] text-[var(--brand-parchment)] relative overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="aspect-[5/3] w-full overflow-hidden">
              <img
                src={TERRAIN_IMG}
                alt="Real EAHORA Juliet in matte black — the actual bike you receive — the whole bike photographed on the road, no props, no overlays."
                className="w-full h-full object-contain editorial-img"
              />
            </div>
            {/* Editorial gold badge — sits BELOW the image now so it can
                never overlap the bike. Previously it was absolutely
                positioned at bottom-right of the image and clipped the
                rear wheel; founder asked it be relocated. */}
            <div className="mt-6 hidden sm:inline-block bg-[var(--brand-gold)] text-[var(--brand-forest-deep)] px-5 py-4 max-w-[260px]">
              <div className="font-display text-3xl leading-none">For every rider.</div>
              <p className="text-xs mt-2 font-semibold uppercase tracking-[0.18em]">
                Not just the fair-weather ones.
              </p>
            </div>
          </div>

          <div>
            <span className="font-eyebrow !text-[var(--brand-gold)]">
              Practical Terrain &amp; Utility Advice
            </span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              The hills belong to <span className="italic text-[var(--brand-gold)]">everyone.</span>
            </h2>
            <p className="mt-6 text-[var(--brand-parchment)]/90 text-lg leading-relaxed">
              The land doesn&rsquo;t ask about your legs, your lungs or your waterproofs. Neither do we.
              What matters up here is whether the bike holds on soft ground when you put a pannier
              of shopping on the rack, whether the walk-mode gets you up the last of the steep bit,
              and whether you can swing your leg over the frame when your trousers are soaking.
              That&rsquo;s the kind of advice we&rsquo;re here for &mdash; and it&rsquo;s only ever a phone call away.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-6">
              {[
                {
                  icon: Anchor,
                  title: 'Robust Kickstand',
                  body:
                    'Twin-leg, wide-footprint. Holds the bike up on soft ground with a full pannier load — no more dropped shopping in the mud.',
                },
                {
                  icon: Footprints,
                  title: '4 kph Walk Mode',
                  body:
                    'A gentle throttle-assist for pushing the bike over a stile, up a wet slope or across a boggy bit. Motor helps your feet do the work.',
                },
                {
                  icon: MessageCircle,
                  title: 'Talk with our team',
                  body:
                    'Message us for a chat about which bike suits your local terrain and your specific needs. No sales pitch — just a Gamekeeper\u2019s judgement.',
                },
              ].map((f) => (
                <div key={f.title} className="border-t border-[var(--brand-parchment)]/25 pt-5">
                  <f.icon size={26} strokeWidth={1.4} className="text-[var(--brand-gold)]" />
                  <div className="mt-3 font-display text-xl">{f.title}</div>
                  <p className="text-sm mt-1.5 opacity-90 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>

            {/* Primary CTA repeated here so the section closes with the ask. */}
            <div className="mt-10">
              <button
                type="button"
                onClick={() => go('contact')}
                data-testid="adaptive-chat-cta"
                className="btn-gold px-6 py-3 text-sm font-semibold tracking-wide inline-flex items-center gap-2 rounded-none"
                aria-label="Talk with our team about which bike is best for your local terrain and your specific needs"
              >
                Talk with our Team
              </button>
              <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-80">
                About which bike suits your local terrain &amp; needs
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
